import { Checkbox } from '@blueprinthq/joy'
import { Box, Heading, Text, VStack } from '@chakra-ui/react'
import { useStoreState } from 'easy-peasy'
import React from 'react'
import {
  useClinicControllerGetClientNotificationPreferences,
  useClinicControllerUpdateClientNotificationPreferences
} from '~/clinician-api'
import {
  type ClinicClientNotificationPreferencesDto,
  type ClinicClientNotificationPreferencesDtoAppointmentsItemsItem,
  ClinicClientNotificationPreferencesDtoAppointmentsItemsItemType
} from '~/clinician-api/models'
import { AppointmentReminderItem } from './appointment-reminder-item'

const FIRST_REMINDER_OPTIONS = [
  { unit: 'weeks', value: 1 },
  { unit: 'weeks', value: 2 },
  { unit: 'days', value: 3 },
  { unit: 'days', value: 2 }
]

const SECOND_REMINDER_OPTIONS = [
  { unit: 'days', value: 1 },
  { unit: 'days', value: 2 },
  { unit: 'hours', value: 12 }
]

const THIRD_REMINDER_OPTIONS = [
  { unit: 'hours', value: 4 },
  { unit: 'hours', value: 2 },
  { unit: 'hours', value: 1 },
  { unit: 'minutes', value: 30 }
]

const getReminderInfo = (
  type: ClinicClientNotificationPreferencesDtoAppointmentsItemsItem['type']
) => {
  if (
    type ===
    ClinicClientNotificationPreferencesDtoAppointmentsItemsItemType.AppointmentsFirstReminder
  ) {
    return {
      label: 'First Reminder',
      options: FIRST_REMINDER_OPTIONS
    }
  }
  if (
    type ===
    ClinicClientNotificationPreferencesDtoAppointmentsItemsItemType.AppointmentsSecondReminder
  ) {
    return {
      label: 'Second Reminder',
      options: SECOND_REMINDER_OPTIONS
    }
  }
  return {
    label: 'Third Reminder',
    options: THIRD_REMINDER_OPTIONS
  }
}

export const ClientNotifications = () => {
  // @ts-expect-error
  const { user } = useStoreState(state => state.auth)

  const { data: clientNotificationPreferences, refetch: refetchPreferences } =
    useClinicControllerGetClientNotificationPreferences(user.clinic_id)

  const { mutateAsync: updateClientNotificationPreferences } =
    useClinicControllerUpdateClientNotificationPreferences()

  const updateAppointmentPreferences = async (
    value: Partial<ClinicClientNotificationPreferencesDto['appointments']>
  ) => {
    if (clientNotificationPreferences) {
      await updateClientNotificationPreferences({
        clinicId: user.clinic_id,
        data: {
          appointments: {
            ...clientNotificationPreferences.appointments,
            ...value
          }
        }
      })
      await refetchPreferences()
    }
  }

  const updateAppointmentReminderItem = async (
    value: Partial<ClinicClientNotificationPreferencesDtoAppointmentsItemsItem>
  ) => {
    if (clientNotificationPreferences) {
      await updateClientNotificationPreferences({
        clinicId: user.clinic_id,
        data: {
          appointments: {
            ...clientNotificationPreferences.appointments,
            items: clientNotificationPreferences.appointments.items.map(
              item => {
                if (item.type === value.type) {
                  return {
                    ...item,
                    ...value
                  }
                }
                return item
              }
            )
          }
        }
      })
      await refetchPreferences()
    }
  }

  if (!clientNotificationPreferences) return null

  return (
    <Box>
      <Box mb={'medium'} w={'100%'}>
        <Heading mb="xxsmall">Client Notifications</Heading>
        <Text>Manage your reminders and notifications for your clients.</Text>
      </Box>

      <Box
        border="1px solid"
        borderColor="pale_gray"
        borderRadius="8px"
        p="medium"
      >
        <Box>
          <Text>
            Blueprint will automatically send appointment reminders to your
            clients base on the selections below.
          </Text>
        </Box>
        <Box mt="medium">
          <Text fontWeight="bold" mb="small">
            Send reminders to:
          </Text>
          <VStack spacing="xsmall" align="flex-start">
            <Checkbox
              checked={
                clientNotificationPreferences.appointments.shouldSendEmail
              }
              defaultChecked={
                clientNotificationPreferences.appointments.shouldSendEmail
              }
              onChange={e => {
                updateAppointmentPreferences({
                  shouldSendEmail: e.target.checked
                })
              }}
              onBlur={() => {}}
            >
              Client's email
            </Checkbox>
            <Checkbox
              checked={clientNotificationPreferences.appointments.shouldSendSms}
              defaultChecked={
                clientNotificationPreferences.appointments.shouldSendSms
              }
              onChange={e => {
                updateAppointmentPreferences({
                  shouldSendSms: e.target.checked
                })
              }}
              onBlur={() => {}}
            >
              Client's SMS
            </Checkbox>
          </VStack>
          <Text mt="medium" fontSize="12px" maxWidth="420px">
            <Text fontSize="12px" as="span" fontWeight="bold">
              Note:
            </Text>{' '}
            These settings won't override your client's own reminder
            preferences. If there's a conflict, we'll follow their selected
            preferences instead.
          </Text>
        </Box>
        <Box mt="medium">
          <Text fontWeight="bold" mb="small">
            How often?
          </Text>

          <VStack spacing="small" align="flex-start">
            {clientNotificationPreferences.appointments.items.map(item => (
              <AppointmentReminderItem
                key={item.type}
                item={item}
                onUpdate={updateAppointmentReminderItem}
              />
            ))}
            <Text pt="xsmall" fontSize="12px" maxWidth="420px">
              <Text fontSize="12px" as="span" fontWeight="bold">
                Note:
              </Text>{' '}
              These reminder settings sync every 10 minutes&mdash;any changes
              you make will take effect shortly.
            </Text>
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}
